/**
 * The platform host map — doc 32 §4.4.
 *
 * THE single source of truth for which hostname serves which part of the app, and
 * what that host's security, indexing, offline and session posture is. Nine
 * production hosts plus the API alias, all served by ONE Next.js app on ONE Vercel
 * project; `proxy.ts` rewrites each host's root onto its `mountPath` in `app/`.
 *
 * Why a table and not nine `if` branches: the same host facts have to agree in at
 * least six places — the proxy router, CSP (`lib/server/csp.ts`), robots, sitemap,
 * canonicals, the CSRF origin allowlist (`lib/server/csrf.ts`) and the billing
 * redirect allowlist. Those had already drifted once when they were written out
 * by hand: `embeddedMenuCsp` silently omitted `connect-src` entries the public
 * menu needed, and `LEGACY_REDIRECT_ORIGINS` in lib/site-url.ts still names three
 * subdomains that stopped serving. Every consumer must DERIVE from this table.
 *
 * No hostname literal appears below. Every one is composed from the registrable
 * domain of `SITE_URL`, so `scripts/check-host-config.mjs` (which forbids
 * `*.menuraq.com` outside lib/site-url.ts) keeps passing and the domain is still
 * written exactly once in the codebase.
 *
 * Client-safe: imports nothing but lib/site-url.ts, which is dependency-free.
 */

// RELATIVE, not the '@/' alias. next.config.ts imports lib/server/csp.ts, which
// imports this file, and Next compiles the config OUTSIDE the app's module
// resolution — the alias is rewritten to './lib/site-url' and fails with
// MODULE_NOT_FOUND, breaking the build before it starts. Anything reachable from
// next.config.ts must use relative imports only (scripts/check-config-imports.mjs).
import { ADMIN_URL, CHOW_URL, SITE_URL } from '../site-url';

/** The registrable domain, derived so it is never written twice. */
const BASE_DOMAIN = new URL(SITE_URL).hostname.replace(/^www\./, '');

const sub = (label: string) => `${label}.${BASE_DOMAIN}`;

// ─── Host identity ───────────────────────────────────────────────────────────

export type PlatformHost =
  | 'www'
  | 'auth'
  | 'dashboard'
  | 'admin'
  | 'manage'
  | 'operations'
  | 'pos'
  | 'courier'
  | 'display'
  | 'kiosk'
  | 'm'
  | 'chow'
  | 'muhid'
  | 'api';

/**
 * Whether the host serves today.
 *
 * `planned` rows are deliberately present before their DNS exists: the invariant
 * check, the CSP builder and the origin allowlists all read this table, and a host
 * that appears only at cutover time is a host whose CSP and robots posture were
 * never reviewed. Doc 32 D7 forbids an env flag gating the cutover, so `status` is
 * edited in a commit — visibly, with the DNS change — never toggled at runtime.
 */
export type HostStatus = 'live' | 'planned';

/** Which CSP profile serves the host. Resolved by `cspFor` (doc 32 §6.1). */
export type HostCspProfile = 'marketing' | 'app' | 'embeddable' | 'locked';

/** The service-worker caching contract (doc 32 §6.3). */
export type HostOfflineContract = 'none' | 'network-first' | 'offline-first' | 'swr';

/**
 * The kind of session the host may hold. `none` means no session cookie is ever
 * set here — the property that contains a public-menu XSS (doc 32 T1/T15).
 */
export type HostSessionKind =
  | 'none'
  | 'issuer'
  | 'staff'
  | 'guest'
  | 'consumer'
  | 'device'
  | 'machine'
  | 'admin';

export interface HostDefinition {
  /** Production hostname. Composed from BASE_DOMAIN, never written literally. */
  hostname: string;
  /**
   * The `app/` path prefix this host's ROOT rewrites onto. `''` means the host
   * serves the app root directly (www). E.g. m.menuraq.com/kofi-kitchen renders
   * app/m/kofi-kitchen.
   */
  mountPath: string;
  /**
   * Top-level path segments belonging to this host. Drives cross-host resolution:
   * a path whose first segment is owned by ANOTHER host 308s there instead of
   * being rewritten into a 404 (doc 32 §4.5, the rule proven by the Chow block in
   * proxy.ts). A path already starting with an owned segment passes through
   * unrewritten.
   */
  owns: readonly string[];
  robots: 'index' | 'noindex';
  csp: HostCspProfile;
  offline: HostOfflineContract;
  session: HostSessionKind;
  /** Installable as a PWA under its own manifest and its own SW scope. */
  installable: boolean;
  status: HostStatus;
  /**
   * Whether this host is the CANONICAL address for the paths it owns — i.e.
   * whether other hosts should redirect to it.
   *
   * Separate from `status` because serving and being canonical are two different
   * events, and for `m.` they must happen weeks apart (doc 32 §8). The migration
   * order is: m. starts serving while www stays canonical → QR generation switches
   * → canonical tags and sitemap move → ONLY THEN the permanent 301.
   *
   * Without this field, flipping `status` to `'live'` would make the router 308
   * `www/m/*` straight to `m.` immediately — performing the irreversible last step
   * at the moment of the first one, and throwing away the crawl signal that tells
   * Google the two URLs are the same page. Redirect-before-canonical is the single
   * most expensive mistake available in this whole plan, so it gets its own switch.
   *
   * Defaults to `true`: a host that serves is normally the canonical home of its
   * own paths. Only a host mid-migration sets it false.
   */
  canonical?: boolean;
  /**
   * The host that permanently REPLACES this one, or absent while it still owns
   * its own address. PROP-02 D2: `manage.` and `operations.` collapse into
   * `dashboard.` — three owner back-offices become one, because an owner working
   * in the same minute across two of them was paying a session hand-off per click
   * (PROP-00 §2).
   *
   * A retired host keeps its hostname alive to answer 301 and nothing else. It
   * stays `status: 'live'` deliberately: the proxy matches a request by hostname
   * first, so a host that stopped being `live` would stop being recognised and
   * the redirect it owes a bookmark would never be served — the bookmark would
   * simply 404, which is the one outcome worse than a redirect.
   *
   * Setting this implies the host is no longer canonical, so `isCanonicalHost`
   * reports false without a second field to keep in step. That matters: the whole
   * collapse only terminates because a retired host is never a redirect TARGET.
   * Leave any of these paths owned by `manage.` and `dashboard.` would 308 the
   * request straight back to the retired host, which would 301 it to
   * `dashboard.` again — two responses per bookmark, the first cached as if the
   * address had moved. Re-owning the paths and retiring the host is ONE change.
   */
  retiredTo?: PlatformHost;
  /** Display name — PWA manifest name and cross-host nav labels. */
  label: string;
  /**
   * What to do with a path whose first segment no host claims.
   *
   * `'mount'` — rewrite it onto `mountPath`. Correct for hosts addressed with SHORT
   * host-relative paths, where `/floor` on pos. legitimately means
   * `/pos/<branch>/floor` and the segment could never be enumerated up front.
   *
   * `'send-home'` — 308 to www. Correct for a host that enumerates every surface it
   * serves in `owns` (Chow), where an unrecognised segment is genuinely foreign and
   * rewriting it would produce a 404 under the mount instead of sending the visitor
   * somewhere real. This reproduces the behaviour the Chow block in proxy.ts already
   * had, and its comment explains why: anything that is not this host's surface must
   * be sent home, never rewritten into a 404.
   */
  unownedPaths?: 'mount' | 'send-home';
  /**
   * Mounts on THIS host that must not be advertised from anywhere else.
   *
   * Every other cross-host mount in this table moves by 308: the visitor asked
   * for `/dashboard` on the wrong host, we know exactly which host serves it, and
   * moving them is kinder than a dead end. That is the wrong call for a mount
   * whose existence is meant to be unguessable — redirecting to it hands over
   * the very hostname it was trying to keep quiet. A host declaring this
   * answers 404 off-host instead, so the path stays reachable at its own
   * address and nowhere else.
   *
   * `muhid` is the only one, and its entries are its own mounts. Keeping the
   * mount secret rather than the whole host matters: the mount still 404s for a
   * visitor who guesses it, but nothing about the redirect table names it.
   *
   * AN ARRAY, because the ops deployment serves two panels — the full console at
   * `/admin` and the reduced support panel at `/support` (blueprint doc 12 §9.1,
   * OD-1: one project, two hidden mounts). A single string could only ever hide
   * one of them, and the second would either have to be published in `owns`
   * (which is the redirect table) or be unreachable on its own host.
   */
  hiddenMounts?: readonly string[];
  /**
   * Where this host's ROOT goes, when the bare mount is not its own page.
   *
   * Only `manage` needs it, and the reason is the mount it shares. Both owner
   * hosts mount at `/dashboard`, and the bare `/dashboard` page is the owner's
   * home — analytics, today's takings — which is dashboard.'s product, not
   * manage.'s. So manage.menuraq.com/ served its neighbour's landing page: the two
   * hosts looked identical at the first URL anyone would type.
   *
   * A redirect rather than a rewrite, deliberately. Rewriting would leave the hub
   * reachable at two addresses on the same host, and the nav highlights by comparing
   * mounted paths — under a rewrite the URL says `/` while the page is `/operations`,
   * so the item the visitor is looking at would not light up.
   */
  home?: string;
}

/**
 * Prefixes that are the SAME on every host and must never be rewritten or bounced.
 *
 * `/api` is host-agnostic by decision (doc 32 D5 keeps first-party calls
 * same-origin). `/_next` is build output. `/handoff` is the cross-host session
 * landing page, which has to resolve identically everywhere or the protocol cannot
 * complete. The rest are files the browser fetches from the host it is on.
 */
export const HOST_AGNOSTIC_PREFIXES: readonly string[] = [
  '/api',
  '/_next',
  '/handoff',
  '/chow-icons',
  '/chow-sw.js',
  '/sw.js',
  '/favicon.ico',
  '/robots.txt',
  '/sitemap.xml',
  '/manifest.json',
  '/manifest.webmanifest',
  '/site.webmanifest',
  '/pos-sw.js',
  '/courier-sw.js',
  '/app-sw.js',
  '/device-sw.js',
  '/offline.html',
  '/.well-known',
];

export function isHostAgnosticPath(pathname: string): boolean {
  return HOST_AGNOSTIC_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

// ─── The table ───────────────────────────────────────────────────────────────
// Ordered as doc 32 §4.1 lists them, so a missing row is obvious on sight.

export const PLATFORM_HOSTS: Record<PlatformHost, HostDefinition> = {
  /** Marketing, docs and legal. The only indexed non-menu surface. */
  www: {
    hostname: new URL(SITE_URL).hostname,
    mountPath: '',
    owns: [
      'about', 'contact', 'features', 'pricing', 'product', 'solutions', 'docs',
      'templates', 'legal', 'terms', 'privacy', 'security', 'cookies',
      'previews', 'preview',
    ],
    robots: 'index',
    csp: 'marketing',
    offline: 'none',
    /**
     * `staff`, NOT `none` — and this is a statement of fact, not an aspiration.
     *
     * www currently serves every owner and POS surface path-based, so it already
     * holds staff sessions. Marking it `none` described the world AFTER the split
     * and broke the transition into it: the hand-off may only target a staff-session
     * host, so a user who signed in on `auth.` and was sent to `www/dashboard` could
     * not acquire a session there, bounced back to sign-in on `auth.` (which was
     * signed in), and looped forever.
     *
     * Reverts to `none` in the same commit that moves the last staff surface off
     * www — at which point www really does stop holding sessions. Until then, an
     * accurate map is what keeps the cutover incremental instead of big-bang.
     */
    session: 'staff',
    installable: false,
    status: 'live',
    label: 'Menuraq',
  },

  /**
   * The identity origin (doc 32 §5.1). Holds the long-lived session and is the
   * ONLY host that may mint a session for another host. The 2FA cookie lives here
   * and nowhere else, which is what keeps the deliberate refusal in
   * lib/server/two-factor.ts to share a cookie domain intact.
   */
  auth: {
    hostname: sub('auth'),
    mountPath: '/auth',
    owns: ['auth'],
    robots: 'noindex',
    csp: 'app',
    offline: 'none',
    session: 'issuer',
    installable: false,
    status: 'live',
    label: 'Menuraq Sign-in',
  },

  /**
   * The light owner account: read the data, read the audits, basic setup.
   * Shares the /dashboard mount with `manage` — the split is per-SURFACE, not per
   * path prefix (doc 32 §4.2), which is exactly why `mount` and `host` are two
   * different fields on SurfaceDefinition.
   */
  dashboard: {
    hostname: sub('dashboard'),
    mountPath: '/dashboard',
    owns: ['dashboard'],
    robots: 'noindex',
    csp: 'app',
    offline: 'network-first',
    session: 'staff',
    installable: true,
    status: 'live',
    label: 'Menuraq Dashboard',
  },

  /** Business administration; no platform-admin principal or shared cookies. */
  admin: {
    hostname: sub('admin'),
    mountPath: '/dashboard/admin',
    owns: [],
    robots: 'noindex',
    csp: 'app',
    offline: 'none',
    session: 'staff',
    installable: false,
    status: 'live',
    label: 'Business Admin',
  },

  /** The deep operational surface: build the catalog, run the pipeline and the people. */
  manage: {
    hostname: sub('manage'),
    mountPath: '/dashboard',
    owns: ['dashboard'],
    robots: 'noindex',
    csp: 'app',
    offline: 'network-first',
    session: 'staff',
    installable: true,
    status: 'live',
    label: 'Menuraq Manage',
    // PROP-02 D2: retired into dashboard. Every path this host used to own is
    // now owned by dashboard. (see DASHBOARD_ROUTE_HOSTS), so the hostname is
    // kept only to 301 the bookmarks that still point at it. `home` is gone with
    // the last path: a retired host answers 301 before any of its own routing
    // runs, so a `home` here would be config nothing could reach.
    retiredTo: 'dashboard',
  },

  /** Deep operations & service: run the floor, manage orders, handle guests. */
  operations: {
    hostname: sub('operations'),
    mountPath: '/dashboard',
    owns: ['dashboard'],
    robots: 'noindex',
    csp: 'app',
    offline: 'network-first',
    session: 'staff',
    installable: true,
    status: 'live',
    label: 'Menuraq Operations',
    // PROP-02 D2: retired into dashboard., same as manage. above.
    retiredTo: 'dashboard',
  },

  /** Till and floor. Offline-first is not a nicety: a till that stops when the wifi drops is not a till. */
  pos: {
    hostname: sub('pos'),
    mountPath: '/pos',
    owns: ['pos'],
    robots: 'noindex',
    csp: 'app',
    offline: 'offline-first',
    session: 'staff',
    installable: true,
    status: 'live',
    label: 'Menuraq POS',
  },

  /** Riders. Offline-first for the same reason as POS, on worse connections. */
  courier: {
    hostname: sub('courier'),
    mountPath: '/courier',
    owns: ['courier'],
    robots: 'noindex',
    csp: 'app',
    offline: 'offline-first',
    session: 'staff',
    installable: true,
    status: 'live',
    label: 'Menuraq Courier',
  },

  /**
   * Unattended screens. `locked` CSP: no form-action, no outward navigation. The
   * board is a separate host from POS precisely so the device that renders it can
   * never reach POS settings — pos/[branchId]/kds/display and settings/signage are
   * the *configuration* of a display and stay on `pos`.
   */
  display: {
    hostname: sub('display'),
    mountPath: '/display',
    owns: ['display', 'order-ready'],
    robots: 'noindex',
    csp: 'locked',
    offline: 'offline-first',
    session: 'device',
    installable: true,
    status: 'live',
    label: 'Menuraq Display',
  },

  /** Unattended self-order. Same lockdown as display. */
  kiosk: {
    hostname: sub('kiosk'),
    mountPath: '/kiosk',
    owns: ['kiosk'],
    robots: 'noindex',
    csp: 'locked',
    offline: 'offline-first',
    session: 'device',
    installable: true,
    status: 'live',
    label: 'Menuraq Kiosk',
  },

  /**
   * The MERCHANT's own storefront — their brand, their theme, Menuraq invisible.
   * Distinct from `chow`, which is Menuraq's marketplace (doc 32 §4.3).
   *
   * The only `embeddable` host in the estate, and therefore the only one with a
   * loose `frame-ancestors`. It holds no staff session at all: that combination
   * is what turns "we hope there is no XSS in a menu item name" into a contained
   * failure (doc 32 P32.1).
   */
  m: {
    hostname: sub('m'),
    mountPath: '/m',
    owns: [
      'm', 'b', 'menu', 'pay', 'receipt', 'r', 'q', 'g', 'book', 'reserve',
      'booking-status', 'invoice', 'quote', 'gift-cards', 'loyalty', 'customers',
    ],
    robots: 'index',
    csp: 'embeddable',
    offline: 'swr',
    session: 'guest',
    installable: true,
    status: 'planned',
    // NOT canonical yet, and this must stay false through step 1 of the migration
    // (doc 32 §8): m. serves while www.menuraq.com/m/* remains the canonical
    // address, so both resolve and no redirect exists. Flipping this to `true` is
    // what turns on the permanent 301 — the irreversible step — and it happens only
    // AFTER canonical tags and the sitemap have moved and been re-submitted.
    canonical: false,
    label: 'Menu',
  },

  // The former platform-admin host was removed (doc 36); the new `admin` row
  // above is merchant administration only. Platform administration is a
  // separate application on its own deployment and its own, unadvertised
  // hostname. It deliberately has no row here: this table feeds the CSP
  // builder and the origin allowlists, and listing the console would both
  // widen those and publish where it lives.

  /**
   * Platform administration (doc 36). Mounted at `/admin`, which is where the
   * console has always lived in `app/` — one Next.js app serves every host, so
   * the route does not move, only the ADDRESS it is reachable at.
   *
   * This row exists to give the console its own address. It deliberately does NOT
   * list `/admin` in `owns`: ownership is what makes one host hand its paths to
   * another with a 308, and announcing `/admin` in this table would hand the
   * hostname to anyone who typed the path — the opposite of a hidden console.
   * `hiddenMounts` says which mounts are served here and 404 everywhere else.
   * `/support` is the second of them: the reduced staff panel (blueprint doc 12),
   * on the same deployment because one Vercel project is one hostname (OD-1).
   *
   * `status: 'live'` and the DNS record belong in the SAME commit, per doc 32 D7 —
   * no env flag may stand in for either. It is already `live` here because a
   * `planned` host is not merely "not yet served": `isCanonicalHost` requires
   * `live`, so a `planned` row is not a legal redirect target at all. Marking it
   * planned was not a conservative default; it made the host unresolvable as a
   * destination while `platformHostForHostname` still matched it, which is the
   * one combination that can only produce a wrong answer.
   *
   * OPERATOR STEP: point `muhid.menuraq.com` at this deployment. Nothing here
   * serves until that record exists.
   *
   * `robots: 'noindex'` — an operator console has no business in a results
   * page, and `noindex` is also the cheap half of "unadvertised": it keeps the
   * hostname out of search results even though the name is now a stated one.
   *
   * `installable: false` so the PWA-manifest invariant stays satisfied without
   * inventing an install target nobody asked for.
   */
  muhid: {
    hostname: new URL(ADMIN_URL).hostname,
    mountPath: '/admin',
    hiddenMounts: ['/admin', '/support'],
    owns: [],
    robots: 'noindex',
    csp: 'app',
    offline: 'none',
    session: 'admin',
    installable: false,
    status: 'live',
    label: 'Platform Admin',
    // This host enumerates exactly one surface. Anything else arriving here is
    // foreign, so send it home rather than rewriting it into a 404 under the
    // mount — the same rule Chow follows.
    unownedPaths: 'send-home',
  },

  /**
   * The consumer marketplace. LIVE — the one host-based flow that already works
   * (proxy.ts). Its routing must not regress when the proxy router is generalised;
   * see doc 32 T18.
   */
  chow: {
    hostname: new URL(CHOW_URL).hostname,
    mountPath: '/chow',
    owns: ['chow', 'discover', 'places', 'orders', 'wallet', 'me', 'insights', 'data-subject', 'scan'],
    robots: 'index',
    csp: 'app',
    offline: 'swr',
    session: 'consumer',
    installable: true,
    status: 'live',
    label: 'Chow',
    // Chow enumerates every surface it serves above, so an unrecognised path is
    // foreign and belongs on www — not rewritten into a 404ing /chow/* route.
    unownedPaths: 'send-home',
  },

  /**
   * MACHINE CLIENTS ONLY (doc 32 D5). First-party `/api` calls stay same-origin on
   * every host — 691 `/api` literals across 227 files, and moving them off-origin
   * would force SameSite=None (undoing the isolation the per-host session model
   * buys) and relax the origin-based CSRF check in proxy.ts from "same origin or
   * reject" to "on a list". That check is the app's primary mutation defence.
   *
   * Serves nothing until doc 24 re-enables /api/v1 — it is hard-404'd today by
   * DISABLED_DEVELOPER_ROUTES in proxy.ts. Reserved now so the public API is never
   * born at the wrong address.
   */
  api: {
    hostname: sub('api'),
    mountPath: '/api',
    owns: ['api'],
    robots: 'noindex',
    csp: 'app',
    offline: 'none',
    session: 'machine',
    installable: false,
    status: 'planned',
    label: 'Menuraq API',
  },
};

export const PLATFORM_HOST_KEYS = Object.keys(PLATFORM_HOSTS) as PlatformHost[];

/**
 * Host assignment for /dashboard routes that are NOT backed by a NavSurface.
 *
 * The surface table covers 32 surfaces; `app/dashboard/**` has 69 pages. The
 * remainder — operations config, profile pages, template editors, onboarding —
 * has to land on exactly one host, or the page becomes reachable at two
 * addresses (doc 32 P5: no page may serve at two addresses).
 *
 * Consulted only AFTER the surface table — a surface's own `host` always wins.
 * Read by `resolveHostForPath` in lib/business-types/surface-definitions.ts; do
 * not re-implement the lookup anywhere else.
 *
 * Since PROP-02 D2 this is one row. It used to be the place that split the owner's
 * pages across three hosts, and the split is what made the table long; the
 * per-surface rows below it in SURFACE_DEFINITIONS still say which surface owns a
 * path, which is the distinction that survived the collapse.
 */
export const DASHBOARD_ROUTE_HOSTS: ReadonlyArray<{ prefix: string; host: PlatformHost }> = [
  // PROP-02 D2 collapsed the three owner hosts into one, and that empties this
  // table. Its whole job was deciding which of dashboard. / manage. / operations.
  // owned each /dashboard path, and with a single owner host every answer is
  // `dashboard` — the catch-all below now says that for every path at once, in one
  // row instead of forty-five.
  //
  // The forty-five rows it used to hold are in git history, not deleted by accident.
  // What is gone is the question they existed to answer, not the record of it.
  //
  // This is a RESOLUTION table, not a serving table: it does not decide whether a
  // path exists. `app/dashboard/**` still has to have the page. What it decides is
  // which host a path belongs to, and there is one host now.
  { prefix: '/dashboard/admin', host: 'admin' },
  { prefix: '/dashboard', host: 'dashboard' },
];

// ─── Lookups ─────────────────────────────────────────────────────────────────

export function hostDefinition(host: PlatformHost): HostDefinition {
  return PLATFORM_HOSTS[host];
}

/**
 * True when the host both serves AND owns the canonical address for its paths.
 *
 * The condition for redirecting other hosts to it. A host that serves but is not
 * canonical (m. during the migration) is reachable directly and never redirected
 * to, so both addresses work and search engines keep their existing signal.
 */
export function isCanonicalHost(host: PlatformHost): boolean {
  const definition = PLATFORM_HOSTS[host];
  if (definition.retiredTo) return false;
  return definition.status === 'live' && definition.canonical !== false;
}

/**
 * The host a retired hostname permanently redirects to, or `null` if it is not
 * retired — or is not a hostname at all.
 *
 * Takes an open string on purpose. Its callers are the guards that decide whether a
 * host is still a destination, and those guards run on values from the wire (a
 * `?to=` query, a `Host` header), where "not a host" is the normal case and not an
 * error. A lookup that threw on a miss would make `isHandoffTarget('evil.example')`
 * a crash instead of a `false`.
 */
export function retiredHostTarget(host: string | null | undefined): PlatformHost | null {
  if (!host) return null;
  const definition = PLATFORM_HOSTS[host as PlatformHost];
  return definition?.retiredTo ?? null;
}

/** `https://<hostname>` for a host. */
export function hostOrigin(host: PlatformHost): string {
  return `https://${PLATFORM_HOSTS[host].hostname}`;
}

/**
 * The canonical public URL for a guest path that `m.` owns — a menu, a branch page,
 * a kiosk link (doc 32 §8).
 *
 * Context-free ON PURPOSE. Every other href builder resolves against "where am I
 * right now", which is correct for navigation and WRONG here: these URLs get
 * printed onto QR codes, embedded in receipts and written into `<link
 * rel="canonical">`. A QR generated from a preview deployment must not encode a
 * preview hostname, and a canonical tag must not depend on which host rendered it.
 *
 * Returns the `m.` address only once `m.` is canonical. Through the migration it
 * keeps returning `www.menuraq.com/m/...`, so codes printed before the flip and
 * codes printed after it both point at an address that resolves — the earlier ones
 * via the permanent 301, forever. That permanence is exactly why this must not
 * switch a moment earlier than the canonical does.
 */
export function guestCanonicalUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;

  // Resolve the OWNING host rather than assuming `m.`: these helpers also build
  // `/kiosk/...` links, and kiosk is its own host. Assuming m. would print a code
  // pointing at a host that will never serve that path.
  const owner = hostForPathSegment(normalized);

  if (owner && isCanonicalHost(owner)) {
    const { mountPath } = PLATFORM_HOSTS[owner];
    const short =
      mountPath && normalized.startsWith(`${mountPath}/`)
        ? normalized.slice(mountPath.length)
        : normalized;
    return new URL(short, hostOrigin(owner)).toString();
  }

  // Until the owner is canonical, the path stays where it has always resolved.
  return new URL(normalized, hostOrigin('www')).toString();
}

/**
 * Which host serves `pathname` as a HIDDEN mount, if any.
 *
 * Separate from the ownership resolvers on purpose. Those answer "who owns this
 * path" for the redirect table, and every answer they give becomes a hostname in
 * a `Location` header. A hidden mount must not do that: it exists, it is served,
 * and off its own host it is a 404. Returning the host from those resolvers would
 * leak it straight back into the behaviour this mount exists to keep quiet, so
 * callers use this to produce a dead end instead.
 */
export function hostForHiddenMount(pathname: string | null | undefined): PlatformHost | null {
  if (!pathname) return null;
  const path = pathname.split(/[?#]/)[0];
  for (const key of PLATFORM_HOST_KEYS) {
    const mounts = PLATFORM_HOSTS[key].hiddenMounts ?? [];
    for (const mount of mounts) {
      if (path === mount || path.startsWith(`${mount}/`)) return key;
    }
  }
  return null;
}

/**
 * The hidden mount `pathname` sits under ON THIS HOST, if any.
 *
 * The counterpart to `hostForHiddenMount`, which answers "whose mount is this
 * anywhere". The router needs this one because a hidden mount is ALREADY an app
 * path: `/support` in the URL is `app/support`, not `app/admin/support`. Without
 * it, the muhid host — `mountPath: '/admin'`, `unownedPaths: 'send-home'` — would
 * treat its own second panel as a foreign path and 308 it to www, where it would
 * 404 by design.
 */
export function hiddenMountForPathOnHost(
  host: PlatformHost,
  pathname: string | null | undefined,
): string | null {
  if (!pathname) return null;
  const path = pathname.split(/[?#]/)[0];
  for (const mount of PLATFORM_HOSTS[host].hiddenMounts ?? []) {
    if (path === mount || path.startsWith(`${mount}/`)) return mount;
  }
  return null;
}

/** Absolute URL for a path on a given host. */
export function hostUrl(host: PlatformHost, path = '/'): string {
  return new URL(path.startsWith('/') ? path : `/${path}`, hostOrigin(host)).toString();
}

/**
 * Which host a production hostname belongs to, or `null` for anything we don't
 * recognise — localhost, `*.localhost`, preview deployments, LAN IPs, tunnels.
 *
 * `null` is load-bearing, not a fallback: unknown hosts must keep serving
 * path-based routing untouched, which is what lets Vercel previews and local dev
 * survive the whole topology change (doc 32 §9). proxy.ts already encodes this
 * decision and its comment warns against "cleaning it up".
 *
 * Matches `planned` hosts too. A host whose DNS has landed but whose `status` has
 * not been flipped should route correctly rather than fall through to www.
 */
export function platformHostForHostname(hostname: string | null | undefined): PlatformHost | null {
  if (!hostname) return null;
  const normalized = hostname.split(':')[0].toLowerCase();
  for (const key of PLATFORM_HOST_KEYS) {
    if (PLATFORM_HOSTS[key].hostname === normalized) return key;
  }
  return null;
}

/**
 * Which host owns a path's first segment.
 *
 * `dashboard` and `manage` both own the `dashboard` segment, so this returns
 * `dashboard` for it by table order. Path-segment ownership is therefore only
 * good enough for "does this path belong on the host it arrived at" — deciding
 * between dashboard and manage requires the SURFACE, which is why
 * SurfaceDefinition carries its own `host` field.
 */
export function hostForPathSegment(pathname: string): PlatformHost | null {
  const segment = pathname.split('/').filter(Boolean)[0];
  if (!segment) return null;
  for (const key of PLATFORM_HOST_KEYS) {
    if (PLATFORM_HOSTS[key].owns.includes(segment)) return key;
  }
  return null;
}

/** True when the path already sits under a segment this host owns (no rewrite needed). */
export function hostOwnsPath(host: PlatformHost, pathname: string): boolean {
  const segment = pathname.split('/').filter(Boolean)[0];
  if (!segment) return false;
  return PLATFORM_HOSTS[host].owns.includes(segment);
}

// ─── Derived origin sets ─────────────────────────────────────────────────────

/**
 * Every first-party origin, live and planned.
 *
 * Planned hosts are included deliberately: a host must be trusted by CSP
 * `connect-src` and the CSRF allowlist BEFORE it serves, or its first request
 * fails in a way that looks like a bug rather than a missing config. This is the
 * exact failure that took down /m sign-in in production — `connect-src` named
 * c.menuraq.com while the active host was chow.menuraq.com, so the browser killed
 * the fetch before dispatch and the only symptom was "Network error".
 */
export const PLATFORM_ORIGINS: readonly string[] = PLATFORM_HOST_KEYS.map(hostOrigin);

/** Origins of hosts that serve today. */
export const LIVE_PLATFORM_ORIGINS: readonly string[] = PLATFORM_HOST_KEYS
  .filter((key) => PLATFORM_HOSTS[key].status === 'live')
  .map(hostOrigin);

/** Hostnames of hosts that serve today, for the proxy's host router. */
export const LIVE_PLATFORM_HOSTNAMES: readonly string[] = PLATFORM_HOST_KEYS
  .filter((key) => PLATFORM_HOSTS[key].status === 'live')
  .map((key) => PLATFORM_HOSTS[key].hostname);

/** Hosts that never hold a session cookie of any kind. */
export const SESSIONLESS_HOSTS: readonly PlatformHost[] = PLATFORM_HOST_KEYS.filter(
  (key) => PLATFORM_HOSTS[key].session === 'none',
);

/** Hosts that hold a staff session and therefore participate in the hand-off (doc 32 §5). */
export const STAFF_SESSION_HOSTS: readonly PlatformHost[] = PLATFORM_HOST_KEYS.filter(
  (key) => PLATFORM_HOSTS[key].session === 'staff',
);
